import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { PROJECT_TYPES } from "@/data/site";
import { Reveal } from "@/components/Primitives";

const EMPTY = { name: "", email: "", project_type: "", message: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const validate = (v) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Please enter your name.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address.";
  if (!v.project_type) e.project_type = "Please choose a project type.";
  if (v.message.trim().length < 10) e.message = "Please write at least 10 characters.";
  return e;
};

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="font-mono text-xs uppercase tracking-[0.2em] text-ash">{label}</label>
    {children}
    {error && <p id={`${id}-error`} role="alert" className="mt-2 text-sm text-[#d98b78]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const ContactForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const formRef = useRef(null);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };
  const aria = (k) => ({ "aria-invalid": Boolean(errors[k]), "aria-describedby": errors[k] ? `contact-${k}-error` : undefined });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      formRef.current?.querySelector(`[name="${first}"]`)?.focus();
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) {
      toast.error("The contact service is not configured. Please use email instead.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${base}/api/contact`, values);
      setStatus("sent");
      setValues(EMPTY);
      toast.success("Message received. Thank you — I'll be in touch.");
    } catch (err) {
      setStatus("idle");
      toast.error("Your message could not be sent. Please try again or use email.");
    }
  };

  return (
    <Reveal delay={0.1} className="relative border border-line bg-coal p-8 sm:p-12">
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-8" data-testid="contact-form">
        <Field id="contact-name" label="Name" error={errors.name}>
          <input id="contact-name" name="name" className="field" autoComplete="name" value={values.name} onChange={set("name")} placeholder="Your name" {...aria("name")} data-testid="contact-name-input" />
        </Field>
        <Field id="contact-email" label="Email" error={errors.email}>
          <input id="contact-email" name="email" type="email" className="field" autoComplete="email" value={values.email} onChange={set("email")} placeholder="you@domain.com" {...aria("email")} data-testid="contact-email-input" />
        </Field>
        <Field id="contact-project_type" label="Project type" error={errors.project_type}>
          <select id="contact-project_type" name="project_type" className="field cursor-pointer appearance-none bg-coal" value={values.project_type} onChange={set("project_type")} {...aria("project_type")} data-testid="contact-project-type-select">
            <option value="" disabled>Select a project type</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field id="contact-message" label="Message" error={errors.message}>
          <textarea id="contact-message" name="message" rows={5} className="field resize-none" value={values.message} onChange={set("message")} placeholder="Tell me about your idea" {...aria("message")} data-testid="contact-message-input" />
        </Field>
        <button type="submit" disabled={status === "sending"} className="btn btn-primary mt-2 justify-center disabled:opacity-70" data-testid="contact-submit-btn">
          {status === "sending" ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <>Send Message <ArrowRight size={16} /></>}
        </button>
        {status === "sent" && (
          <p role="status" className="flex items-center gap-3 text-sm text-ivory" data-testid="contact-success-message">
            <Check size={16} className="text-gold" /> Message received. I'll reply as soon as I can.
          </p>
        )}
      </form>
    </Reveal>
  );
};
